import moment from "moment-timezone";
import { CalendarEvent, SlotStyle } from "components/Calendar/types";
import { Participant, AvailabilitySuggestedTime } from "./types";

function busyPeriodColor(participant: Participant, source?: string): string {
  if (source === "reservation") return "blue.300";
  if (source === "camino_meetings") return "orange.400";
  if (source === "google_calendar") return "red.400";
  // Fallback for periods without a source tag (Camino-only users)
  return participant.availabilitySource === "camino_meetings" ? "orange.400" : "red.400";
}

export function busyPeriodsToCalendarEvents(participant: Participant): CalendarEvent[] {
  return participant.busyPeriods
    .filter((bp) => bp.source !== "current_meeting")
    .map((bp, index) => ({
      id: `busy-${participant.id ?? participant.name}-${index}`,
      title: bp.label,
      start: new Date(bp.startTime),
      end: new Date(bp.endTime),
      canResize: false,
      canDrag: false,
      style: {
        bg: busyPeriodColor(participant, bp.source),
        opacity: 0.85,
        border: "none",
      },
    }));
}

export function suggestedTimesToCalendarEvents(suggestedTimes: AvailabilitySuggestedTime[]): CalendarEvent[] {
  return suggestedTimes.map((st, index) => ({
    id: `suggested-${index}`,
    title: `Suggested (Score: ${st.score})`,
    start: new Date(st.startTime),
    end: new Date(st.endTime),
    canResize: false,
    canDrag: false,
    style: {
      bg: "teal.200",
      opacity: 0.4,
      border: "1px dashed",
    },
  }));
}

function isOutsideWorkingHours(time: Date, participant: Participant): boolean {
  if (!participant.workingHours || !participant.timezone) return false;

  const participantMoment = moment.tz(time, participant.timezone);
  const timeStr = participantMoment.format("HH:mm");

  return timeStr < participant.workingHours.start || timeStr >= participant.workingHours.end;
}

export function timeToPosition(time: Date, slotHeight: number, timezone: string): number {
  const m = moment.tz(time, timezone);
  return (m.hours() + m.minutes() / 60) * slotHeight;
}

export function durationToHeight(durationMinutes: number, slotHeight: number): number {
  return (durationMinutes / 60) * slotHeight;
}

export function buildGetSlotStyle(participants: Participant[], schedulingTimezone: string) {
  return (time: Date, colIndex: number): SlotStyle | undefined => {
    const participant = participants[colIndex];
    if (!participant) return undefined;

    // BackgroundGrid creates `time` as h:mm in the column's local timezone.
    // Re-interpret that same h:mm in the scheduling timezone so working-hours
    // comparisons align with the visual timeline (where row h = h:00 scheduling TZ).
    const colTz = participant.timezone || schedulingTimezone;
    const colMoment = moment.tz(time, colTz);
    const checkTime = moment.tz({ hour: colMoment.hour(), minute: colMoment.minute() }, schedulingTimezone).toDate();

    if (isOutsideWorkingHours(checkTime, participant)) {
      return { bg: "gray.100", opacity: 0.5 };
    }

    return undefined;
  };
}
